// Dynamic field-definition builder.
// Merges live systemFields from the FMX /post-options API with a thin static
// enrichment layer that carries only what the API doesn't provide: field types,
// UI groups, crossSheet relationships, lookup endpoints, and special handling.
//
// This replaces the fully-hardcoded FMX_API_STANDARD_FIELDS in fmxFieldSchema.js
// and the FMX_FIELD_MAP / FMX_ID_LOOKUP_FIELDS in fmxEndpoints.js.
//
// Enrichment keys for synthetic fields must match GET OPTIONS sortKey names
// so the synthetic field mechanism in syncFmxDataForProject picks them up.

// --- Enrichment registry ---
// Keyed by [schemaType][systemField.key]. Each entry stores ONLY what the API
// doesn't tell us. Unlisted fields default to { type: 'string' }.
//
// Shape:
//   type?:       'number' | 'date' | 'email' | 'time' | 'boolean' | 'stringArray'  (default 'string')
//   group?:      string                         (default 'Core Fields')
//   crossSheet?: string                         (schema type for dep validation)
//   label?:      string                         (override API label)
//   lookup?:     { endpoint, searchParam, isArray? }  (for ID-resolution fields)
//   special?:    string                         (e.g. 'assetConditionEnum'; 'assignment'
//                fields are mappable + ID-resolvable but excluded from the main POST
//                payload — StepExport posts them to the assignments sub-endpoint)

// FMX asset-condition enum (Fmx.Model FilterAssetCondition). NotSpecified=1
// clears the condition; there is no "retired" condition — retiring equipment
// is a separate endpoint, so 'retired' cells are warned about and omitted.
export const ASSET_CONDITION_MAP = {
  'not specified': 1,
  'notspecified': 1,
  'unknown': 1,
  'critical': 2,
  'poor': 3,
  'fair': 4,
  'good': 5,
  'excellent': 6,
};
export const ASSET_CONDITION_UNSUPPORTED = ['retired'];

// Human-readable asset-condition labels, in the order FMX presents them.
// Used to tell Claude the allowed values for the enum (the post-options
// system field carries no options array).
export const ASSET_CONDITION_LABELS = ['Excellent', 'Good', 'Fair', 'Poor', 'Critical', 'Not specified'];

// FMX asset-category enum (Fmx.Model.Equipments.AssetCategory, byte-valued).
// On READ the API returns the display label; on WRITE it expects the integer.
// Both the display spelling ('Backflow Preventer') and the C# enum spelling
// ('Backflow Preventor') map to the same value. Keys are lower-cased.
export const ASSET_CATEGORY_MAP = {
  'packaged rooftop unit': 0,
  'steam boiler': 1,
  'hydronic boiler': 2,
  'modular ahu': 3,
  'hydronic pump': 4,
  'chiller': 5,
  'exhaust fan': 6,
  'domestic hot water heater': 7,
  'mini-split ac unit': 8,
  'mini split ac unit': 8,
  'backflow preventer': 9,
  'backflow preventor': 9,
  'building automation system': 10,
  'sump pump': 11,
};

// Display labels in enum order — the option hint shown to Claude.
export const ASSET_CATEGORY_LABELS = [
  'Packaged Rooftop Unit', 'Steam Boiler', 'Hydronic Boiler', 'Modular AHU',
  'Hydronic Pump', 'Chiller', 'Exhaust Fan', 'Domestic Hot Water Heater',
  'Mini-Split AC Unit', 'Backflow Preventer', 'Building Automation System', 'Sump Pump',
];

// Requisitions expose their custom-field values under `detailsCustomFields`
// on PurchaseOrderRequisitionEditModel; every other entity uses `customFields`.
export function getCustomFieldsPayloadKey(baseType) {
  return baseType === 'Requisition' ? 'detailsCustomFields' : 'customFields';
}

// Bump whenever enrichment field keys change shape (renames, removals, type
// changes that alter payloads). fmx_reference_cache rows stamped with an older
// version are treated as stale so pushes never use retired synthetic keys.
export const ENRICHMENT_VERSION = 2;

const FMX_FIELD_ENRICHMENTS = {
  'Building': {
    // isRequired entries on enrichments are a FALLBACK: when the API's
    // post-options response reports `isRequired`, that wins (see
    // buildFieldDefinitions). Enrichment covers entities whose post-options
    // endpoint is missing or doesn't use the systemFields shape — notably
    // Resource, which only exposes get-options without isRequired flags.
    name:                                  { label: 'Name', isRequired: true },
    address:                               { label: 'Address' },
    latitude:                              { label: 'Latitude', type: 'number' },
    longitude:                             { label: 'Longitude', type: 'number' },
    phone:                                 { label: 'Phone' },
    taxRate:                               { label: 'Tax rate', type: 'number' },
    sundayOperatingHoursStartTime:         { label: 'Sunday From', type: 'time', group: 'Operating Hours' },
    sundayOperatingHoursEndTime:           { label: 'Sunday To', type: 'time', group: 'Operating Hours' },
    mondayOperatingHoursStartTime:         { label: 'Monday From', type: 'time', group: 'Operating Hours' },
    mondayOperatingHoursEndTime:           { label: 'Monday To', type: 'time', group: 'Operating Hours' },
    tuesdayOperatingHoursStartTime:        { label: 'Tuesday From', type: 'time', group: 'Operating Hours' },
    tuesdayOperatingHoursEndTime:          { label: 'Tuesday To', type: 'time', group: 'Operating Hours' },
    wednesdayOperatingHoursStartTime:      { label: 'Wednesday From', type: 'time', group: 'Operating Hours' },
    wednesdayOperatingHoursEndTime:        { label: 'Wednesday To', type: 'time', group: 'Operating Hours' },
    thursdayOperatingHoursStartTime:       { label: 'Thursday From', type: 'time', group: 'Operating Hours' },
    thursdayOperatingHoursEndTime:         { label: 'Thursday To', type: 'time', group: 'Operating Hours' },
    fridayOperatingHoursStartTime:         { label: 'Friday From', type: 'time', group: 'Operating Hours' },
    fridayOperatingHoursEndTime:           { label: 'Friday To', type: 'time', group: 'Operating Hours' },
    saturdayOperatingHoursStartTime:       { label: 'Saturday From', type: 'time', group: 'Operating Hours' },
    saturdayOperatingHoursEndTime:         { label: 'Saturday To', type: 'time', group: 'Operating Hours' },
  },

  'Equipment Type': {
    name:                 { label: 'Name', isRequired: true },
    areMetersTracked:     { label: 'Track meters', type: 'boolean' },
    isDowntimeTracked:    { label: 'Track downtime', type: 'boolean' },
    isAssetLifespanTracked: { label: 'Track asset lifespan', type: 'boolean' },
  },

  'Equipment': {
    tag:                          { label: 'Tag', isRequired: true },
    equipmentTypeID:              { label: 'Type', crossSheet: 'Equipment Type', lookup: { endpoint: '/v1/equipment-types', searchParam: 'search' }, isRequired: true },
    buildingID:                   { label: 'Building', crossSheet: 'Building', lookup: { endpoint: '/v1/buildings', searchParam: 'search' }, isRequired: true },
    locationResourceID:           { label: 'Location', lookup: { endpoint: '/v1/resources', searchParam: 'search' } },
    parentEquipmentID:            { label: 'Parent Equipment', lookup: { endpoint: '/v1/equipment', searchParam: 'search' } },
    inventoryItemIDs:             { label: 'Inventory items', lookup: { endpoint: '/v1/inventory', searchParam: 'search', isArray: true } },
    assignedUserIDs:              { label: 'Assigned users', lookup: { endpoint: '/v1/users', searchParam: 'search', isArray: true } },
    downtimeCalculationStartDate: { label: 'Downtime calculation start date', type: 'date' },
    // Asset lifespan (Capital Planner). Only permitted when the org has
    // Capital Planner enabled and the equipment type tracks asset lifespan;
    // assetCategory/assetCondition write as integer enums (see the maps above).
    installationDate:             { label: 'Installation date', type: 'date', group: 'Asset Lifespan' },
    estimatedEndOfLife:           { label: 'Estimated end-of-life', type: 'date', group: 'Asset Lifespan' },
    plannedReplacementDate:       { label: 'Planned replacement date', type: 'date', group: 'Asset Lifespan' },
    replacementAssetValue:        { label: 'Replacement asset value', type: 'number', group: 'Asset Lifespan' },
    capacity:                     { label: 'Capacity', type: 'number', group: 'Asset Lifespan' },
    assetCategory:                { label: 'Asset category', special: 'assetCategoryEnum', group: 'Asset Lifespan' },
    assetCondition:               { label: 'Asset Condition', special: 'assetConditionEnum', group: 'Asset Lifespan' },
  },

  'Inventory': {
    name:              { label: 'Name', isRequired: true },
    inventoryTypeID:   { label: 'Type', lookup: { endpoint: '/v1/inventory-types', searchParam: 'search' }, isRequired: true },
    buildingID:        { label: 'Building', crossSheet: 'Building', lookup: { endpoint: '/v1/buildings', searchParam: 'search' }, isRequired: true },
    locationResourceID:{ label: 'Location', lookup: { endpoint: '/v1/resources', searchParam: 'search' } },
    currentQuantity:   { label: 'Current quantity', type: 'number' },
    sku:               { label: 'SKU' },
    minimumQuantity:   { label: 'Minimum quantity', type: 'number' },
    unitPrice:         { label: 'Unit price', type: 'number' },
    // `barcodeNumber` removed: InventoryItemEditModel has no such property —
    // the API silently dropped it.
  },

  'Resource': {
    name:                              { label: 'Name', isRequired: true },
    buildingID:                        { label: 'Building', crossSheet: 'Building', lookup: { endpoint: '/v1/buildings', searchParam: 'search' }, isRequired: true },
    address:                           { label: 'Address' },
    latitude:                          { label: 'Latitude', type: 'number' },
    longitude:                         { label: 'Longitude', type: 'number' },
    // ResourceEditModel.ResourceTypes takes an array of type NAMES (strings),
    // not IDs — no lookup; values pass straight through. crossSheet wires the
    // names into Validate-step dependency checking against the live cache.
    resourceTypes:                     { label: 'Resource type', type: 'stringArray', crossSheet: 'Resource Type' },
    isLocation:                        { label: 'Is location', type: 'boolean' },
    // `capacity` and the per-day *AvailabilityStartTime/EndTime fields were
    // removed: ResourceEditModel has no such properties — the API silently
    // dropped them (resource availability is not settable via the public API).
    // Schedule Request Settings — all verified against ResourceEditModel.
    schedulable:                       { label: 'Schedulable', type: 'boolean', group: 'Schedule Request Settings' },
    scheduleRequestRequiresApproval:   { label: 'Requires approval', type: 'boolean', group: 'Schedule Request Settings' },
    scheduleRequestRequiresEstimating: { label: 'Requires estimating', type: 'boolean', group: 'Schedule Request Settings' },
    scheduleRequestRequiresInvoicing:  { label: 'Requires invoicing', type: 'boolean', group: 'Schedule Request Settings' },
    scheduleRequestQuantity:           { label: 'Quantity', type: 'number', group: 'Schedule Request Settings' },
    scheduleRequestDisableConflicts:   { label: 'Disable conflicts', type: 'boolean', group: 'Schedule Request Settings' },
    scheduleRequestPermittedUserTypeIDs: { label: 'Permitted user types', group: 'Schedule Request Settings', lookup: { endpoint: '/v1/user-types', searchParam: 'search', isArray: true } },
    // The FMX import spreadsheet's "Approval order" (Standard / After hours) columns are
    // NOT importable: scheduleRequestStandardApprovalTiers / scheduleRequestAfterHoursApprovalTiers
    // exist only in the API's read model, not the POST/PUT edit model. The same goes for
    // `area` and the approval line IDs seen in FMX audit logs (auto-created side effects).
    // Scheduling rate Cost/Unit fields are generated per rate type from dynamicRates in
    // App.js (apiKey `schedulingRates:<name>:<cost|unit>`), composed into the
    // schedulingRates array in transformRowToPayload — not enrichment entries.
    transportationRequestIsPickupLocation: { label: 'Pickup location', type: 'boolean', group: 'Transportation Request Settings' },
  },

  'User': {
    name:                      { label: 'Name', isRequired: true },
    // Not marked required here: the API only requires email for non-contact
    // users (post-options / admin overrides can re-tighten).
    email:                     { label: 'Email', type: 'email' },
    userTypeID:                { label: 'User type', lookup: { endpoint: '/v1/user-types', searchParam: 'search' }, isRequired: true },
    accessibleBuildingIDs:     { label: 'Building access', crossSheet: 'Building', lookup: { endpoint: '/v1/buildings', searchParam: 'search', isArray: true } },
    phone:                     { label: 'Phone' },
    isContact:                 { label: 'Is contact', type: 'boolean' },
    isSupplier:                { label: 'Is supplier', type: 'boolean' },
    password:                  { label: 'Password' },
    assignedEquipmentItemIDs:  { label: 'Assigned Equipment', lookup: { endpoint: '/v1/equipment', searchParam: 'search', isArray: true } },
    // `laborRate`, `canBeDriver`, and `requirePasswordChange` removed:
    // UserEditModel has no such properties — the API silently dropped them.
  },

  'Work Request': {
    name:              { label: 'Name', isRequired: true },
    buildingID:        { label: 'Building', crossSheet: 'Building', lookup: { endpoint: '/v1/buildings', searchParam: 'search' }, isRequired: true },
    requestTypeID:     { label: 'Request Type', lookup: { endpoint: '/v1/request-types', searchParam: 'search' }, isRequired: true },
    locationResourceID:{ label: 'Location', lookup: { endpoint: '/v1/resources', searchParam: 'search' } },
    otherLocation:     { label: 'Other Location' },
    latitude:          { label: 'Latitude', type: 'number' },
    longitude:         { label: 'Longitude', type: 'number' },
    onBehalfOfUserID:  { label: 'On Behalf Of', lookup: { endpoint: '/v1/users', searchParam: 'search' } },
    equipmentItemIDs:  { label: 'Equipment Items', lookup: { endpoint: '/v1/equipment', searchParam: 'search', isArray: true } },
    dueDate:           { label: 'Due Date', type: 'date' },
    // Self-referential lookups: the {module} token is resolved at call time in
    // buildIdCache using the current row's schemaType. Self-references are always
    // within the same work-request module.
    parentRequestID:   { label: 'Parent Request', lookup: { endpoint: '/v1/{module}-requests', searchParam: 'search' } },
    childRequestIDs:   { label: 'Child Requests', lookup: { endpoint: '/v1/{module}-requests', searchParam: 'search', isArray: true } },
    blockedByRequestIDs: { label: 'Blocked By', lookup: { endpoint: '/v1/{module}-requests', searchParam: 'search', isArray: true } },
    blockingRequestIDs:  { label: 'Blocking', lookup: { endpoint: '/v1/{module}-requests', searchParam: 'search', isArray: true } },
    // scheduledTimeBlock is a nested object on WorkRequestEditModel (a plain
    // string fails deserialization) — dot-notation composed in transform, same
    // pattern as Schedule Request's firstOccurrenceEventTimeBlock.
    'scheduledTimeBlock.startDate':    { label: 'Scheduled Start Date', type: 'date' },
    'scheduledTimeBlock.endDate':      { label: 'Scheduled End Date', type: 'date' },
    'scheduledTimeBlock.startTimeUtc': { label: 'Scheduled Start Time' },
    'scheduledTimeBlock.endTimeUtc':   { label: 'Scheduled End Time' },
    followingUserIDs:  { label: 'Following Users', lookup: { endpoint: '/v1/users', searchParam: 'search', isArray: true } },
    signature:         { label: 'Signature' },
    // Assignment fields: mappable and name→ID resolvable, but excluded from the
    // main POST body — StepExport chains them to POST {endpoint}/{id}/assignments
    // (WorkRequestAssignmentEditModel = { assignedUsers: int[], priorityLevel }).
    // Labels must match FMX_ASSIGNMENT_FIELDS in fmxEndpoints.js.
    assignedUsers:     { label: 'Assigned Users', special: 'assignment', lookup: { endpoint: '/v1/users', searchParam: 'search', isArray: true } },
    priorityLevel:     { label: 'Priority Level', special: 'assignment' },
    // TODO (CLAUDE.md §8b): `requestedInventoryLogs` (array) is documented on
    // WorkRequestEditModel but not yet surfaced here. Add when a user needs to import
    // inventory-log entries alongside a work request; shape per §8b is underspecified.
  },

  'Schedule Request': {
    name:               { label: 'Name', isRequired: true },
    requestTypeID:      { label: 'Request Type', lookup: { endpoint: '/v1/request-types', searchParam: 'search' }, isRequired: true },
    buildingIDs:        { label: 'Buildings', crossSheet: 'Building', lookup: { endpoint: '/v1/buildings', searchParam: 'search', isArray: true }, isRequired: true },
    resourceQuantities: { label: 'Resources', lookup: { endpoint: '/v1/resources', searchParam: 'search', isArray: true } },
    otherResource:      { label: 'Other Resource' },
    isPrivate:          { label: 'Is Private', type: 'boolean' },
    // 'event-time'/'reservation-time'/'setup-duration'/'teardown-duration' were
    // removed: they are get-options sortKeys, not ScheduleRequestEditModel
    // properties — the API silently dropped them. Event times are importable
    // via the firstOccurrenceEventTimeBlock fields below.
    // firstOccurrenceEventTimeBlock (flat fields — dot-notation composed in transform)
    'firstOccurrenceEventTimeBlock.startDate':    { label: 'Event Start Date',     type: 'date' },
    'firstOccurrenceEventTimeBlock.endDate':      { label: 'Event End Date',       type: 'date' },
    'firstOccurrenceEventTimeBlock.startTimeUtc': { label: 'Event Start Time' },
    'firstOccurrenceEventTimeBlock.endTimeUtc':   { label: 'Event End Time'   },
    // schedule / recurrence (flat fields — dot-notation composed in transform).
    // Keys match ScheduleEditModel property names; labels preserved from the
    // previous (wrongly-keyed) fields so saved column mappings carry over.
    'schedule.frequency':                { label: 'Recurrence Frequency' },
    'schedule.interval':                 { label: 'Recurrence Interval',  type: 'number' },
    'schedule.weeklyDaysOfWeek':         { label: 'Recurrence Days', type: 'stringArray' },
    'schedule.monthlyMode':              { label: 'Monthly Recurrence' },
    'schedule.terminal':                 { label: 'Recurrence Ends' },
    'schedule.terminalEndDate':          { label: 'Recurrence End Date',  type: 'date' },
    'schedule.terminalOccurrenceCount':  { label: 'Recurrence End Count', type: 'number' },
  },

  'Work Task': {
    name:              { label: 'Name', isRequired: true },
    requestTypeID:     { label: 'Request Type', lookup: { endpoint: '/v1/request-types', searchParam: 'search' }, isRequired: true },
    instructionSetID:  { label: 'Instruction Set', lookup: { endpoint: '/v1/{module}/instruction-sets', searchParam: 'search' } },
    mode:              { label: 'Mode' },
    nextDueDate:       { label: 'Next Due Date', type: 'date' },
    buildingIDs:       { label: 'Buildings', crossSheet: 'Building', lookup: { endpoint: '/v1/buildings', searchParam: 'search', isArray: true } },
    locationResourceID:{ label: 'Location', lookup: { endpoint: '/v1/resources', searchParam: 'search' } },
    equipmentItemIDs:  { label: 'Equipment Items', lookup: { endpoint: '/v1/equipment', searchParam: 'search', isArray: true } },
    assignedUserIDs:   { label: 'Assigned Users', lookup: { endpoint: '/v1/users', searchParam: 'search', isArray: true } },
    otherLocation:     { label: 'Other Location' },
  },

  'Transportation Request': {
    name:                        { label: 'Name', isRequired: true },
    requestTypeID:               { label: 'Request Type', lookup: { endpoint: '/v1/request-types', searchParam: 'search' }, isRequired: true },
    buildingID:                  { label: 'Building', crossSheet: 'Building', lookup: { endpoint: '/v1/buildings', searchParam: 'search' }, isRequired: true },
    pickupLocationResourceID:    { label: 'Pickup Location', lookup: { endpoint: '/v1/resources', searchParam: 'search' } },
    pickupLocation:              { label: 'Pickup Location Text' },
    onBehalfOfUserID:            { label: 'On Behalf Of', lookup: { endpoint: '/v1/users', searchParam: 'search' } },
    destination:                 { label: 'Destination' },
    departureTimeUtc:            { label: 'Departure Time', type: 'date' },
    returnTimeUtc:               { label: 'Return Time', type: 'date' },
  },

  'Accounting Account': {
    name: { label: 'Name', isRequired: true },
    // Type-only enrichments (no label, so post-options labels/mappings are
    // untouched): ensures Yes/No cells coerce to real booleans.
    isDebit:  { type: 'boolean' },
    isCredit: { type: 'boolean' },
  },

  'Requisition': {
    title:            { label: 'Title', isRequired: true },
    requestTypeID:    { label: 'Request Type', lookup: { endpoint: '/v1/request-types', searchParam: 'search' }, isRequired: true },
    dueDate:          { label: 'Due Date', type: 'date' },
    memo:             { label: 'Memo' },
    supplierUserID:   { label: 'Supplier', lookup: { endpoint: '/v1/users', searchParam: 'search' } },
    supplierName:     { label: 'Supplier Name' },
    supplierEmail:    { label: 'Supplier Email', type: 'email' },
    supplierAddress:  { label: 'Supplier Address' },
    billToName:       { label: 'Bill To Name' },
    billToBuildingID: { label: 'Bill To Building', crossSheet: 'Building', lookup: { endpoint: '/v1/buildings', searchParam: 'search' } },
    billToAddress:    { label: 'Bill To Address' },
    shipToName:       { label: 'Ship To Name' },
    shipToBuildingID: { label: 'Ship To Building', crossSheet: 'Building', lookup: { endpoint: '/v1/buildings', searchParam: 'search' } },
    shipToAddress:    { label: 'Ship To Address' },
    followingUserIDs: { label: 'Following Users', lookup: { endpoint: '/v1/users', searchParam: 'search', isArray: true } },
    shippingCost:     { label: 'Shipping Cost', type: 'number' },
    discount:         { label: 'Discount', type: 'number' },
  },

  'Inventory Adjustment': {
    inventoryID:          { label: 'Inventory Item', crossSheet: 'Inventory', lookup: { endpoint: '/v1/inventory', searchParam: 'search' }, isRequired: true },
    // [Required] on AdjustedInventoryLogEditModel — without it rows pass local
    // validation then 400 at the API.
    name:                 { label: 'Name', isRequired: true },
    adjustment:           { label: 'Adjustment', type: 'number', isRequired: true },
    transactionUnitPrice: { label: 'Unit Price', type: 'number' },
    updateUnitPrice:      { label: 'Update Unit Price', type: 'boolean' },
    description:          { label: 'Description' },
  },

  'Inventory Transfer': {
    inventoryID:          { label: 'From Inventory Item', crossSheet: 'Inventory', lookup: { endpoint: '/v1/inventory', searchParam: 'search' }, isRequired: true },
    otherInventoryItemID: { label: 'To Inventory Item', crossSheet: 'Inventory', lookup: { endpoint: '/v1/inventory', searchParam: 'search' }, isRequired: true },
    // [Required] on TransferredInventoryLogEditModel.
    name:                 { label: 'Name', isRequired: true },
    transferredQuantity:  { label: 'Quantity', type: 'number', isRequired: true },
    transferToOtherItem:  { label: 'Transfer To Other Item', type: 'boolean' },
    transactionUnitPrice: { label: 'Unit Price', type: 'number' },
    updateUnitPrice:      { label: 'Update Unit Price', type: 'boolean' },
    description:          { label: 'Description' },
  },

  'Equipment Log': {
    equipmentID:   { label: 'Equipment', crossSheet: 'Equipment', lookup: { endpoint: '/v1/equipment', searchParam: 'search' }, isRequired: true },
    name:          { label: 'Name', isRequired: true },
    cost:          { label: 'Cost', type: 'number' },
    description:   { label: 'Description' },
  },

  // UtilityProviderEditModel is { name, repeatInterval, services: [...] }.
  // The 'Utility Service' fields below match UtilityServiceEditModel property
  // names exactly and are kept flat for mapping; the payload finalizer in
  // fmxTransform nests them under services[] before push.
  'Utility Provider': {
    name:                         { label: 'Name', isRequired: true },
    repeatInterval:               { label: 'Repeat Interval' },
    utilityTypeID:                { label: 'Utility Type', type: 'number', group: 'Utility Service' },
    isConsumptionTracked:         { label: 'Track Consumption', type: 'boolean', group: 'Utility Service' },
    consumptionUnit:              { label: 'Consumption Unit', group: 'Utility Service' },
    isConsumptionSplitByMeter:    { label: 'Consumption Split By Meter', type: 'boolean', group: 'Utility Service' },
    isDemandTracked:              { label: 'Track Demand', type: 'boolean', group: 'Utility Service' },
    demandUnit:                   { label: 'Demand Unit', group: 'Utility Service' },
    isDemandSplitByMeter:         { label: 'Demand Split By Meter', type: 'boolean', group: 'Utility Service' },
    isEnergyUseIntensityTracked:  { label: 'Track Energy Use Intensity', type: 'boolean', group: 'Utility Service' },
    kbtuConversionFactor:         { label: 'KBTU Conversion Factor', type: 'number', group: 'Utility Service' },
    isWaterUseIntensityTracked:   { label: 'Track Water Use Intensity', type: 'boolean', group: 'Utility Service' },
    gallonConversionFactor:       { label: 'Gallon Conversion Factor', type: 'number', group: 'Utility Service' },
  },
};

// --- Builder ---

/**
 * Build field definitions by merging live systemFields from /post-options with
 * static enrichments. Returns an array matching the shape used throughout the app:
 *   { name, apiKey, required, type, crossSheet?, group?, maximumLength?,
 *     lookupConfig?, isPermitted }
 *
 * @param {string} schemaType  Base schema type (no module qualifier)
 * @param {Array}  systemFields  From /post-options: [{ key, label, isRequired, isPermitted, maximumLength }]
 * @returns {Array} Field definitions
 */
export function buildFieldDefinitions(schemaType, systemFields, overrides = null, moduleQualifiedSchemaType = null) {
  const enrichments = FMX_FIELD_ENRICHMENTS[schemaType];
  if (!enrichments || !Array.isArray(systemFields) || systemFields.length === 0) return null;

  // Cached systemFields carry the synthetic entries from sync time. If the
  // enrichment registry gained entries since the cache was written (and
  // ENRICHMENT_VERSION wasn't bumped to invalidate it), synthesize the missing
  // labeled entries here so new fields always render without a cache refresh.
  const systemFieldKeys = new Set(systemFields.map(sf => sf.key));
  const mergedSystemFields = [...systemFields];
  for (const [key, enrich] of Object.entries(enrichments)) {
    if (enrich.label && !systemFieldKeys.has(key)) {
      mergedSystemFields.push({ key, label: enrich.label, isRequired: enrich.isRequired === true, isPermitted: true });
    }
  }

  // Admin overrides resolved per-field below. Prefer a module-qualified row
  // (e.g. "Work Task:fit-inspections") over the base-schema row so admins
  // can tighten rules for a specific module without affecting siblings.
  const qualifiedOverrides = overrides && moduleQualifiedSchemaType
    ? overrides[moduleQualifiedSchemaType] : null;
  const baseOverrides = overrides ? overrides[schemaType] : null;

  const defs = [];

  for (const sf of mergedSystemFields) {
    if (sf.isPermitted === false) continue;

    const enrich = enrichments[sf.key] || {};
    const isLookup = !!enrich.lookup;

    // Precedence: admin override > API /post-options > enrichment default.
    // `is_required === null` on an override row means "no opinion, defer".
    const ov = qualifiedOverrides?.[sf.key] ?? baseOverrides?.[sf.key];
    let required;
    if (ov && ov.is_required !== null && ov.is_required !== undefined) {
      required = !!ov.is_required;
    } else {
      required = sf.isRequired || enrich.isRequired || false;
    }

    // Assignment fields never enter the main POST payload (apiKey null even
    // without a lookup); StepExport posts them to the assignments sub-endpoint.
    const isAssignment = enrich.special === 'assignment';

    const def = {
      name: enrich.label || sf.label,
      apiKey: isLookup || isAssignment ? null : sf.key,
      required,
      type: enrich.type || 'string',
    };

    if (enrich.crossSheet) def.crossSheet = enrich.crossSheet;
    if (enrich.group) def.group = enrich.group;
    if (enrich.special) def.special = enrich.special;
    if (sf.maximumLength) def.maximumLength = sf.maximumLength;

    if (isLookup) {
      def.lookupConfig = {
        endpoint: enrich.lookup.endpoint,
        idField: sf.key,
        searchParam: enrich.lookup.searchParam,
        isArray: enrich.lookup.isArray || false,
      };
      if (isAssignment) def.lookupConfig.assignmentOnly = true;
    }

    defs.push(def);
  }

  return defs;
}

/**
 * Check whether enrichments exist for a given schema type.
 */
export function hasEnrichments(schemaType) {
  return !!FMX_FIELD_ENRICHMENTS[schemaType];
}

// --- Derived maps (drop-in replacements for FMX_FIELD_MAP / FMX_ID_LOOKUP_FIELDS) ---

/**
 * Derive a { [fieldName]: apiKey } map from dynamic field definitions.
 * Covers non-lookup fields only (where apiKey !== null).
 */
export function deriveFieldMap(fieldDefs) {
  const map = {};
  for (const f of fieldDefs) {
    if (f.apiKey) map[f.name] = f.apiKey;
  }
  return map;
}

/**
 * Derive a { [fieldName]: { endpoint, idField, searchParam, isArray } } map
 * from dynamic field definitions. Covers lookup fields only.
 */
export function deriveLookupFields(fieldDefs) {
  const map = {};
  for (const f of fieldDefs) {
    if (f.lookupConfig) map[f.name] = f.lookupConfig;
  }
  return map;
}

export { FMX_FIELD_ENRICHMENTS };
